import PropTypes from 'prop-types'
import React from 'react'
import undefsafe from 'undefsafe'
import configSite from '../../../../config/sites'

import siteConfig from '../../../../common/utils/siteConfig'

import { toTitle } from '../../../../common/utils/formatString'
import { getCurrentSiteDomain } from '../../../../common/utils/sites'
import {
  reportProductClick,
  reportProductAddToCartAttempt,
  reportModalPageView
} from '../../../../common/analytics/resultAnalytics'
import { logError } from '../../../../common/utils/errors'

export const getItemSmallPreviewUrl = (item) => {
  const previewPaths = undefsafe(configSite.defaults, 'ui.item_small_preview_paths')
  return previewPaths
    .map(path => undefsafe(item.previews, path))
    // return first non falsy value
    .reduce((prevPath, currPath) => {
      if (prevPath) {
        return prevPath
      }
      return currPath
    })
}

export const formattedItem = (item, additionalProps = {}) => {
  let nextItem = Object.assign({}, item, additionalProps)
  // price
  // TODO: price_cents is missing from photodue search result...
  nextItem.price = item.price_cents ? item.price_cents.toString().slice(0, -2) : ''
  // classification
  nextItem.formattedClassification = item.classification && typeof item.classification === 'string'
    ? item.classification.split('/').map(category => toTitle(category)).join(' / ')
    : ''
  return nextItem
}

export const StarRating = ({
    rating,
    className,
    fullStarClass = 'star-rating__star--full',
    halfStarClass = 'star-rating__star--half',
    emptyStarClass = 'star-rating__star--empty',
    maxStars = 5 }) => {
  let stars = []
  let roundedRating = Math.round(rating * 2) / 2.0
  for (let i = 0; i < Math.floor(roundedRating); i++) {
    stars.push(fullStarClass)
  }

  if (roundedRating % 1) {
    stars.push(halfStarClass)
  }

  while (stars.length < maxStars) {
    stars.push(emptyStarClass)
  }

  return (
    <div className={className}>
      {stars.map((starClass, index) => {
        return <b key={index} className={starClass} />
      })}
    </div>
  )
}

StarRating.propTypes = {
  rating: PropTypes.number.isRequired,
  className: PropTypes.string,
  fullStarClass: PropTypes.string,
  halfStarClass: PropTypes.string,
  emptyStarClass: PropTypes.string,
  maxStars: PropTypes.number
}

export const MetaCategories = ({className, classification}) => {
  const categories = classification.split('/')
  return (
    <span className={className}>
      in {categories.map((category, index) => {
        const slash = index === categories.length - 1 ? null : ' / '
        return (<span key={index}><b>{toTitle(category)}</b>{slash}</span>)
      })}
    </span>
  )
}

MetaCategories.propTypes = {
  className: PropTypes.string,
  classification: PropTypes.string.isRequired
}

// https://github.com/envato/foundation-docs/blob/master/docs/shopfront_special_ui_rules.md
export const filterSortByOptions = (currentSite, parsedLocation, options) => {
  let filteredOptions = [...options]
  if (currentSite === 'photodune') {
    filteredOptions = filteredOptions.filter(options => {
      return options.value !== 'rating'
    })
  }

  // no `Best Match` if there's no search term
  if (!parsedLocation.term) {
    filteredOptions = filteredOptions.filter(options => {
      return options.value !== 'relevance'
    })
  }
  return filteredOptions
}

const getFooterLink = (item, currentSite) => {
  const footerLink = siteConfig(currentSite, 'item_view.footer_link')
  return footerLink && {
    label: footerLink.label,
    href: footerLink.href.replace(/\{(.*)\}/g, (match, field) => {
      return item[field]
    })
  }
}

export const itemDecorator = (currentSite, openModal, signedOut, favoriteCollectionID, collections, deleteBookmark, addBookmark, createFavoriteCollection, pageSize, cartItems, location) => {
  return (item, index) => {
    const rank = index + (((Number(location.query.page) || 1) - 1) * pageSize) + 1
    const reportProductClickHandler = reportProductClick.bind(null, item, rank, currentSite)
    const reportProductAddToCartAttemptHandler = reportProductAddToCartAttempt.bind(null, item, rank, currentSite)
    const nonFavoriteCollections = Object.keys(collections)
      .map(collectionID => collections[collectionID])
      .filter(collection => collection.special_role !== 'favorite')
    const isItemInFavorites = collections[favoriteCollectionID] &&
      collections[favoriteCollectionID].bookmarks[item.id] &&
      collections[favoriteCollectionID].bookmarks[item.id].optimisticStatus !== 'deleted'
    const enhancedItem = formattedItem(item, {
      ui: {
        isInFavourites: isItemInFavorites,
        isInBookmarks: nonFavoriteCollections.some(collection => collection.bookmarks[item.id]),
        isInCart: cartItems.some(cartItemId => cartItemId.item_id === item.id),
        smallPreviewUrl: getItemSmallPreviewUrl(item),
        footerLink: getFooterLink(item, currentSite)
      }
    })
    const onAddToFavourites = (event) => {
      event.preventDefault()
      if (signedOut) {
        window.location = `//${getCurrentSiteDomain(currentSite)}/sign_in?action_name=favorite&resource_id=${item.id}&return_to=${window.location.pathname}${window.location.search}`
      } else {
        // check whether user has favorite collection
        if (favoriteCollectionID) {
          if (isItemInFavorites) {
            const bookmark = collections[favoriteCollectionID].bookmarks[item.id]
            if (bookmark.id && bookmark.optimisticStatus !== 'pending') {
              const isFavouriteCollection = true
              deleteBookmark(favoriteCollectionID, item.id, bookmark.id, isFavouriteCollection)
            }
          } else {
            const isFavouriteCollection = true
            addBookmark(favoriteCollectionID, item.id, isFavouriteCollection)
          }
        } else {
          createFavoriteCollection()
            .then((action) => {
              if (action && action.type === 'createCollectionSuccess') {
                const newCollectionId = action.payload.id
                // this is used for ga tracking so we know it's add to favourite collection
                const isFavouriteCollection = true
                return addBookmark(
                  newCollectionId,
                  item.id,
                  isFavouriteCollection
                )
              }
            }).catch(logError)
        }
      }
    }
    const onAddToCollections = (event) => {
      event.preventDefault()
      if (signedOut) {
        window.location = `//${getCurrentSiteDomain(currentSite)}/sign_in?action_name=collection&resource_id=${item.id}&return_to=${window.location.pathname}${window.location.search}`
      } else {
        reportModalPageView({
          title: 'Modal: Add item to collection',
          page: `${window.location.pathname}/add-item-to-collection${window.location.search}`
        })
        openModal({
          type: 'addItemToCollection',
          payload: {
            item
          }
        })
      }
    }
    const onAddToCart = (event) => {
      event.preventDefault()
      reportProductAddToCartAttemptHandler()

      reportModalPageView({
        title: 'Modal: Customize item before adding to cart',
        page: `${window.location.pathname}/customize-item-before-adding-to-cart${window.location.search}`
      })
      openModal({
        type: 'addItemToCart',
        payload: {
          item: enhancedItem
        }
      })
    }

    return {
      item: enhancedItem,
      rank,
      onItemLinkClick: reportProductClickHandler,
      onAddToCart,
      onAddToFavourites,
      onAddToCollections
    }
  }
}



// WEBPACK FOOTER //
// ./client/pages/search/result/utils.js